import { useCallback, useEffect, useRef } from "react";
import useLocationAutocomplete from "../hooks/useLocationAutocomplete";

/**
 * ARIA-compliant combobox for birth-location search powered by
 * OpenStreetMap Nominatim (free, no API key).
 *
 * Props:
 *  - value          : string (controlled text)
 *  - onChange(text)  : called when the user types
 *  - onSelect(item) : called when a suggestion is picked
 *                     item = { placeId, displayName, lat, lon }
 *  - onBlur()       : optional
 *  - placeholder    : string
 *  - autoFocus      : bool
 *  - selectedItem   : the currently-selected item (or null)
 */
export default function LocationAutocomplete({
  value,
  onChange,
  onSelect,
  onBlur,
  placeholder = "e.g., Katugastota, Sri Lanka",
  autoFocus = false,
  selectedItem: externalSelected,
}) {
  const {
    suggestions,
    isLoading,
    error,
    activeIndex,
    setActiveIndex,
    isOpen,
    setIsOpen,
    select,
    selectedItem,
    clear,
  } = useLocationAutocomplete(value || "");

  const inputRef = useRef(null);
  const listRef = useRef(null);
  const notifiedRef = useRef(null);

  // Editing the text abandons the picked place, so the field can be cleared
  // and a new place searched for.
  const handleInputChange = useCallback((e) => {
    if (selectedItem) clear();
    onChange?.(e.target.value);
  }, [onChange, selectedItem, clear]);

  // Notify the parent once per new pick. Comparing against the parent's copy
  // instead re-fired after every keystroke and wrote the place name back over
  // whatever the user had typed.
  useEffect(() => {
    if (selectedItem && selectedItem !== notifiedRef.current) {
      notifiedRef.current = selectedItem;
      onChange?.(selectedItem.displayName); // Update the input text
      onSelect?.(selectedItem);
    }
  }, [selectedItem, onChange, onSelect]);

  // A place picked on an earlier visit to this step still counts as selected.
  const shownItem = selectedItem || externalSelected;

  // Scroll active item into view
  useEffect(() => {
    if (activeIndex >= 0 && listRef.current) {
      const items = listRef.current.querySelectorAll('[role="option"]');
      items[activeIndex]?.scrollIntoView({ block: "nearest" });
    }
  }, [activeIndex]);

  // Close dropdown when clicking outside
  const containerRef = useRef(null);
  useEffect(() => {
    function handleClickOutside(e) {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [setIsOpen]);

  // Keyboard navigation
  const handleKeyDown = useCallback(
    (e) => {
      if (!isOpen || suggestions.length === 0) {
        if (e.key === "ArrowDown" && suggestions.length > 0) {
          setIsOpen(true);
          setActiveIndex(0);
          e.preventDefault();
        }
        return;
      }

      switch (e.key) {
        case "ArrowDown":
          e.preventDefault();
          setActiveIndex((i) =>
            i < suggestions.length - 1 ? i + 1 : 0
          );
          break;
        case "ArrowUp":
          e.preventDefault();
          setActiveIndex((i) =>
            i > 0 ? i - 1 : suggestions.length - 1
          );
          break;
        case "Enter":
          e.preventDefault();
          if (activeIndex >= 0 && activeIndex < suggestions.length) {
            select(suggestions[activeIndex]);
          }
          break;
        case "Escape":
          e.preventDefault();
          setIsOpen(false);
          setActiveIndex(-1);
          break;
        case "Tab":
          setIsOpen(false);
          setActiveIndex(-1);
          break;
        default:
          break;
      }
    },
    [isOpen, suggestions, activeIndex, setActiveIndex, setIsOpen, select]
  );

  const handleItemClick = useCallback(
    (item) => {
      select(item);
      inputRef.current?.focus();
    },
    [select]
  );

  const listboxId = "location-listbox";

  return (
    <div ref={containerRef} className="relative">
      {/* Input */}
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          role="combobox"
          aria-autocomplete="list"
          aria-expanded={isOpen}
          aria-controls={listboxId}
          aria-activedescendant={
            activeIndex >= 0 ? `location-option-${activeIndex}` : undefined
          }
          value={value || ""}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={() => {
            if (suggestions.length > 0 && !selectedItem) setIsOpen(true);
          }}
          onBlur={(e) => {
            // Don't fire if clicking into the dropdown
            if (containerRef.current?.contains(e.relatedTarget)) return;
            onBlur?.();
          }}
          placeholder={placeholder}
          autoFocus={autoFocus}
          autoComplete="off"
          className={
            "w-full rounded-2xl bg-white border border-gray-300 px-4 py-4 pr-10 text-gray-900 " +
            "placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-orange-500 " +
            "focus:border-transparent"
          }
        />

        {/* Spinner / Check icon */}
        <span className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
          {isLoading ? (
            <svg
              className="animate-spin h-5 w-5 text-orange-500"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
              />
            </svg>
          ) : shownItem ? (
            <svg
              className="h-5 w-5 text-green-500"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fillRule="evenodd"
                d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                clipRule="evenodd"
              />
            </svg>
          ) : (
            <svg
              className="h-5 w-5 text-gray-400"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fillRule="evenodd"
                d="M5.05 4.05a7 7 0 119.9 9.9 7 7 0 01-9.9-9.9zM10 3a7 7 0 100 14 7 7 0 000-14zm-1 4a1 1 0 112 0v3.586l1.707 1.707a1 1 0 01-1.414 1.414l-2-2A1 1 0 019 10.586V7z"
                clipRule="evenodd"
              />
            </svg>
          )}
        </span>
      </div>

      {/* Error message */}
      {error && (
        <p className="mt-1 text-xs text-rose-600" role="alert">
          {error}
        </p>
      )}

      {/* Dropdown */}
      {isOpen && suggestions.length > 0 && (
        <ul
          id={listboxId}
          ref={listRef}
          role="listbox"
          aria-label="Location suggestions"
          className={
            "absolute z-50 mt-1 w-full max-h-64 overflow-y-auto rounded-xl bg-white " +
            "border border-gray-200 shadow-lg py-1"
          }
        >
          {suggestions.map((item, idx) => (
            <li
              key={item.placeId}
              id={`location-option-${idx}`}
              role="option"
              aria-selected={idx === activeIndex}
              onMouseDown={(e) => e.preventDefault()} // prevent input blur
              onClick={() => handleItemClick(item)}
              onMouseEnter={() => setActiveIndex(idx)}
              className={
                "flex items-start gap-3 px-4 py-3 cursor-pointer transition-colors " +
                (idx === activeIndex
                  ? "bg-orange-50 text-orange-900"
                  : "text-gray-700 hover:bg-gray-50")
              }
            >
              {/* Pin icon */}
              <svg
                className="mt-0.5 h-4 w-4 flex-shrink-0 text-orange-500"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z"
                  clipRule="evenodd"
                />
              </svg>
              <div className="min-w-0">
                <span className="block text-sm font-medium truncate">
                  {highlightMatch(item.displayName, value || "")}
                </span>
                <span className="block text-xs text-gray-400 truncate">
                  {item.lat.toFixed(4)}°, {item.lon.toFixed(4)}°
                </span>
              </div>
            </li>
          ))}
        </ul>
      )}

      {/* Powered by OSM attribution (required by Nominatim TOS) */}
      {isOpen && suggestions.length > 0 && (
        <div className="absolute z-50 w-full text-right pr-3 mt-0">
          <span className="text-[10px] text-gray-400">
            Powered by{" "}
            <a
              href="https://www.openstreetmap.org/copyright"
              target="_blank"
              rel="noopener noreferrer"
              className="underline hover:text-gray-600"
            >
              OpenStreetMap
            </a>
          </span>
        </div>
      )}

      {/* Selected location chip */}
      {shownItem && (
        <div className="mt-2 flex items-center gap-2 rounded-lg bg-orange-50 border border-orange-200 px-3 py-2 text-sm text-orange-800">
          <svg
            className="h-4 w-4 flex-shrink-0 text-orange-500"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fillRule="evenodd"
              d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z"
              clipRule="evenodd"
            />
          </svg>
          <span className="truncate font-medium">{shownItem.displayName}</span>
          <span className="text-xs text-orange-500 ml-auto whitespace-nowrap">
            {shownItem.lat.toFixed(4)}°, {shownItem.lon.toFixed(4)}°
          </span>
        </div>
      )}
    </div>
  );
}

/**
 * Highlight the part of `text` that matches `query` (case-insensitive).
 */
function highlightMatch(text, query) {
  if (!query || query.length < 2) return text;
  const idx = text.toLowerCase().indexOf(query.toLowerCase());
  if (idx === -1) return text;
  return (
    <>
      {text.slice(0, idx)}
      <mark className="bg-orange-100 text-orange-900 rounded px-0.5">
        {text.slice(idx, idx + query.length)}
      </mark>
      {text.slice(idx + query.length)}
    </>
  );
}
